import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";

import DstButton from "../../components/DstButton/DstButton";
import DstConfirm from "../../components/DstConfirm/DstConfirm";
import Popup from "../../components/Popup/Popup";
import { useSession } from "../../contexts/SessionContext";
import translate from "../../services/Translate";
import { JobBlock } from "./LandingPageJobBlocks.d";
import { deleteJobBlock, fetchJobBlocks } from "./LandingPageJobBlocks.function";
import AddCardJobBlock from "./components/AddCardJobBlock/AddCardJobBlock";
import CardJobBlock from "./components/CardJobBlock/CardJobBlock";
import EditJobBlockForm from "./components/EditJobBlockModal/EditJobBlockModal";
import ReorderJobBlocksModal from "./components/ReorderJobBlocksModal/ReorderJobBlocksModal";

const LandingPageJobBlocks = () => {
  const { language } = useSession();
  const [jobBlockList, setJobBlockList] = useState<JobBlock[]>([]);
  const [selectedJobBlock, setSelectedJobBlock] = useState<JobBlock | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [jobBlockConfirmDeletion, setJobBlockConfirmDeletion] = useState<JobBlock | null>(null);
  const [showReorderModal, setShowReorderModal] = useState(false);

  useEffect(() => {
    fetchJobBlocks(setJobBlockList, language, setIsLoading);
  }, [language]);

  return (
    <React.Fragment>
      <Helmet>
        <title>Landing page job blocks management</title>
      </Helmet>
      <div className="m-3 mx-md-5">
        <div className="d-flex flex-wrap justify-content-between gap-2">
          <h1>{translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.JOB_BLOCKS.TITLE")}</h1>
          <div className="d-flex justify-content-end mb-4 gap-2">
            <DstButton
              value={translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.JOB_BLOCKS.BUTTON.ADD")}
              clickFunction={() => setShowAddModal(true)}
            />
            <DstButton
              value={translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.JOB_BLOCKS.BUTTON.REORDER")}
              variant="secondary"
              clickFunction={() => setShowReorderModal(true)}
            />
          </div>
        </div>

        <Row className="g-4">
          {isLoading
            ? [...Array(8)].map((_, index) => (
                <Col key={`mock-${index}`} sm={6} lg={4} xl={3}>
                  <CardJobBlock language={language} mock jobBlock={{} as JobBlock} />
                </Col>
              ))
            : jobBlockList.map((jobBlock) => (
                <Col key={jobBlock.id} sm={6} lg={4} xl={3}>
                  <CardJobBlock
                    language={language}
                    jobBlock={jobBlock}
                    onEdit={() => setSelectedJobBlock(jobBlock)}
                    onDelete={() => setJobBlockConfirmDeletion(jobBlock)}
                  />
                </Col>
              ))}
        </Row>

        <AddCardJobBlock
          show={showAddModal}
          onClose={() => setShowAddModal(false)}
          onCreated={() => fetchJobBlocks(setJobBlockList, language)}
          language={language}
        />

        {selectedJobBlock && (
          <Popup
            show={true}
            onClose={() => setSelectedJobBlock(null)}
            modalTitle={translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.JOB_BLOCKS.EDIT_TITLE").replace(
              "{{TITLE}}",
              selectedJobBlock.title
            )}
            size="lg"
          >
            <EditJobBlockForm
              jobBlock={selectedJobBlock}
              onCancel={() => setSelectedJobBlock(null)}
              onSaved={() => {
                fetchJobBlocks(setJobBlockList, language);
                setSelectedJobBlock(null);
              }}
              language={language}
            />
          </Popup>
        )}

        {jobBlockConfirmDeletion && (
          <DstConfirm
            show
            onHide={() => setJobBlockConfirmDeletion(null)}
            onConfirm={() => {
              deleteJobBlock(jobBlockConfirmDeletion.id, language, () => {
                setJobBlockList((previousList) =>
                  previousList.filter((jobBlock) => jobBlock.id !== jobBlockConfirmDeletion.id)
                );
                setJobBlockConfirmDeletion(null);
              });
            }}
            title={translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.JOB_BLOCKS.DELETE.TITLE")}
            messageExplain={translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.JOB_BLOCKS.DELETE.EXPLAIN")}
            messageConfirm={translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.JOB_BLOCKS.DELETE.CONFIRM").replace(
              "{{TITLE}}",
              jobBlockConfirmDeletion.title
            )}
            confirmTextButton={translate(language, "GLOBAL.DELETE")}
          />
        )}
      </div>
      <ReorderJobBlocksModal
        show={showReorderModal}
        onClose={() => setShowReorderModal(false)}
        jobBlocks={jobBlockList}
        language={language}
        onSaved={() => {
          fetchJobBlocks(setJobBlockList, language);
        }}
      />
    </React.Fragment>
  );
};

export default LandingPageJobBlocks;
